/**
 * ACCOUNTS.md §4 — 「找回手机」(`/account/phone`).
 *
 * 手机可选。绑好以后，忘记密码时可以自己收一条短信验证码。没配短信时页面说明原因。
 */
import { useState } from "react";
import { Link } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Card, Field, Icon, Input, Skeleton, Tag, useConfirm, useToast } from "@xinghai/ui";
import { accountPhoneApi } from "../../lib/api";
import { friendlyMessage } from "../../lib/apiErrors";
import { qk } from "../../lib/queries";

const PHONE_RE = /^1[3-9]\d{9}$/;

function when(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString("zh-CN", { hour12: false });
}

export function PhonePage() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");

  const q = useQuery({
    queryKey: qk.accountPhone,
    queryFn: ({ signal }) => accountPhoneApi.get(signal),
  });

  const start = useMutation({
    mutationFn: (value: string) => accountPhoneApi.start(value),
    onSuccess: (data) => {
      qc.setQueryData(qk.accountPhone, data);
      setCode("");
      toast.success("验证码已经发到手机上了。");
    },
    onError: (err) => toast.error(friendlyMessage(err, "验证码没能发出去，请稍后再试。")),
  });

  const verify = useMutation({
    mutationFn: (value: string) => accountPhoneApi.verify(value),
    onSuccess: (data) => {
      qc.setQueryData(qk.accountPhone, data);
      void qc.invalidateQueries({ queryKey: qk.me });
      setPhone("");
      setCode("");
      toast.success("手机已绑定。以后忘记密码可以自己找回了。");
    },
    onError: (err) => toast.error(friendlyMessage(err, "验证码不对，或者已经过期了。")),
  });

  const remove = useMutation({
    mutationFn: () => accountPhoneApi.remove(),
    onSuccess: (data) => {
      qc.setQueryData(qk.accountPhone, data);
      void qc.invalidateQueries({ queryKey: qk.me });
      toast.success("已经解绑。");
    },
    onError: (err) => toast.error(friendlyMessage(err, "解绑没有成功，请重试。")),
  });

  if (q.isPending) {
    return (
      <div className="xz-container xz-stack" aria-busy="true">
        <Skeleton variant="rect" height={120} />
      </div>
    );
  }

  const state = q.data;

  return (
    <div className="xz-container xz-container--readable xz-stack">
      <h1 className="gz-type-heading">找回手机</h1>
      <p className="gz-muted">
        绑一个中国大陆手机号（可选）。绑好以后，忘记密码时可以自己收到短信验证码，不用再找老师。
      </p>

      {state && !state.sms ? (
        <Card padding="md">
          <p className="gz-notice" role="status">
            <Icon id="ui-info" size={20} />
            这套部署没有开通短信服务，暂时不能绑定手机。可以改绑邮箱，或者找课程老师要一个一次性重置链接。
          </p>
        </Card>
      ) : (
        <>
          <Card title="当前手机" padding="md">
            {state?.phone ? (
              <div className="xz-stack">
                <p>
                  <Tag tone="success">
                    <Icon id="ui-check-circle" size={16} /> 已验证
                  </Tag>{" "}
                  <strong data-testid="account-phone">{state.phone}</strong>
                </p>
                <p className="gz-caption">验证时间：{when(state.verified_at)}</p>
                <Button
                  variant="danger"
                  loading={remove.isPending}
                  onClick={async () => {
                    const ok = await confirm({
                      title: "解绑这个手机号？",
                      message: "解绑后就不能再用短信自己找回密码了。",
                      confirmLabel: "解绑",
                      danger: true,
                    });
                    if (ok) remove.mutate();
                  }}
                >
                  <Icon id="ui-trash" size={20} /> 解绑
                </Button>
              </div>
            ) : (
              <p className="gz-muted">还没有绑定手机。</p>
            )}
          </Card>

          {state?.pending && (
            <Card title="填写验证码" padding="md">
              <form
                className="xz-stack"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (/^\d{6}$/.test(code.trim())) verify.mutate(code.trim());
                }}
              >
                <p className="gz-caption">
                  验证码已发到 <strong>{state.pending.phone}</strong>，10 分钟内有效。
                </p>
                <Field label="6 位验证码">
                  <Input
                    name="code"
                    value={code}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  />
                </Field>
                <Button
                  type="submit"
                  variant="primary"
                  block
                  loading={verify.isPending}
                  disabled={!/^\d{6}$/.test(code.trim())}
                >
                  验证并绑定
                </Button>
              </form>
            </Card>
          )}

          <Card title={state?.phone ? "换一个手机号" : "绑定手机"} padding="md">
            <form
              className="xz-stack"
              onSubmit={(e) => {
                e.preventDefault();
                if (PHONE_RE.test(phone)) start.mutate(phone);
              }}
            >
              <Field label="手机号" hint="11 位中国大陆号码">
                <Input
                  name="phone"
                  inputMode="numeric"
                  autoComplete="tel"
                  value={phone}
                  maxLength={11}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                />
              </Field>
              <Button
                type="submit"
                variant={state?.pending ? "secondary" : "primary"}
                block
                loading={start.isPending}
                disabled={!PHONE_RE.test(phone)}
              >
                发送验证码
              </Button>
            </form>
          </Card>
        </>
      )}

      <p className="gz-caption">
        <Link to="/account/email">找回邮箱</Link> · <Link to="/account/password">修改密码</Link>
      </p>
    </div>
  );
}
